import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Input,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import Swal from 'sweetalert2';
import React, { useCallback, useEffect, useState } from 'react';
import EnhancedTableHead from '../../../Common/Component/EnhancedTableHead';
import SelectSearch from '../../../Common/Component/SelectSearch';
import { EJE_THEME } from '../../../Common/Constant';
import * as request from '../../../Common/Util/Request';
import './index.scss';

const MOCK_INSTRUCTORS = [
  { id: 1, name: '이상환', specialty: '자기소개서 & 면접', isVisible: true },
  { id: 2, name: '윤선미', specialty: '기획제작', isVisible: true },
  { id: 3, name: '마케팅멘토', specialty: '마케팅', isVisible: false },
];

const HEAD_CELLS = [
  { id: 'id', label: 'ID', align: 'center', minWidth: 50 },
  { id: 'name', label: '강사', align: 'left', minWidth: 160 },
  { id: 'specialty', label: '전문분야', align: 'left', minWidth: 160 },
  { id: 'tutorId', label: '연결된 강사', align: 'center', minWidth: 120 },
  { id: 'isVisible', label: '노출 상태', align: 'center', minWidth: 120 },
  { id: 'actions', label: '관리', align: 'center', minWidth: 100 },
];

function avatarColor(name) {
  const colors = ['#F24462', '#7B61FF', '#0091FF', '#00B087', '#FF8C00'];
  const code = [...(name || '?')].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return colors[code % colors.length];
}

function InstructorList() {
  const [instructors, setInstructors] = useState([]);
  const [total, setTotal] = useState(0);
  const [usingMock, setUsingMock] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sortInfo, setSortInfo] = useState({ name: 'id', order: 'asc' });
  const [inputText, setInputText] = useState('');
  const [keyword, setKeyword] = useState('');
  const [tutorOptions, setTutorOptions] = useState([]);

  useEffect(() => {
    request.getAdminTutors().then((res) => {
      const tutors = res?.data?.tutors ?? [];
      setTutorOptions([
        { name: '— 매핑 없음 —', value: '' },
        ...tutors.map((t) => ({ name: `${t.name} (id: ${t.id})`, value: String(t.id) })),
      ]);
    });
  }, []);

  const onChangeTutor = useCallback(
    (instructor, value) => {
      const tutorId = value === '' ? null : Number(value);
      const prevTutorId = instructor.tutorId;
      const prevTutorName = instructor.tutorName;
      setInstructors((prev) =>
        prev.map((it) =>
          it.id === instructor.id
            ? {
                ...it,
                tutorId,
                tutorName: tutorOptions.find((o) => o.value === value)?.name?.replace(/ \(id: \d+\)$/, '') || null,
              }
            : it,
        ),
      );
      request.updateInstructorTutor(instructor.id, tutorId).catch(() => {
        setInstructors((prev) =>
          prev.map((it) => (it.id === instructor.id ? { ...it, tutorId: prevTutorId, tutorName: prevTutorName } : it)),
        );
      });
    },
    [tutorOptions],
  );

  const loadData = useCallback(({ pageIndex, pageSize, kw, sort, order }) => {
    request
      .getAdminInstructors({
        page: pageIndex + 1,
        limit: pageSize,
        ...(kw && { keyword: kw }),
        sort,
        order,
      })
      .then((res) => {
        const payload = res?.data ?? res;
        const rows = payload?.instructors ?? payload?.rows ?? [];
        setInstructors(rows);
        setTotal(payload?.total ?? payload?.count ?? rows.length);
        setUsingMock(false);
      })
      .catch(() => {
        const kwLower = kw?.trim().toLowerCase() ?? '';
        const filtered = kwLower
          ? MOCK_INSTRUCTORS.filter(
              (i) => i.name?.toLowerCase().includes(kwLower) || i.specialty?.toLowerCase().includes(kwLower),
            )
          : MOCK_INSTRUCTORS;
        setInstructors(filtered);
        setTotal(filtered.length);
        setUsingMock(true);
      });
  }, []);

  useEffect(() => {
    loadData({ pageIndex: page, pageSize: rowsPerPage, kw: keyword, sort: sortInfo.name, order: sortInfo.order });
  }, [page, rowsPerPage, keyword, sortInfo]);

  const onClickSearch = useCallback(() => {
    setPage(0);
    setKeyword(inputText);
  }, [inputText]);

  const handleRequestSort = useCallback((_, property) => {
    setSortInfo((prev) => ({
      name: property,
      order: prev.name === property && prev.order === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const onToggleVisibility = useCallback(
    (instructor) => {
      const next = !instructor.isVisible;
      setInstructors((prev) => prev.map((it) => (it.id === instructor.id ? { ...it, isVisible: next } : it)));
      request.updateInstructorVisibility(instructor.id, next).catch(() => {
        setInstructors((prev) => prev.map((it) => (it.id === instructor.id ? { ...it, isVisible: !next } : it)));
        if (!usingMock) {
          Swal.fire({
            html: `<div style="margin-top:20px;font-weight:bold;">노출 여부 변경에 실패했습니다.</div>`,
            toast: true,
            position: 'center',
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            icon: 'error',
          });
        }
      });
    },
    [usingMock],
  );

  const onClickDelete = useCallback((instructor) => {
    Swal.fire({
      title: '정말 삭제하시겠습니까?',
      text: `'${instructor.name}' 강사를 삭제합니다. 삭제한 데이터는 복구할 수 없습니다.`,
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      preConfirm: () =>
        request.deleteInstructor(instructor.id).then(() => {
          setInstructors((prev) => prev.filter((it) => it.id !== instructor.id));
          setTotal((prev) => Math.max(0, prev - 1));
          Swal.fire({
            html: `<div style="margin-top:20px;font-weight:bold;">삭제되었습니다.</div>`,
            toast: true,
            position: 'center',
            timer: 3000,
            timerProgressBar: true,
            showConfirmButton: false,
            icon: 'success',
          });
        }),
    });
  }, []);

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="instructor-list">
        {/* 페이지 헤더 */}
        <div className="instructor-list__header">
          <div>
            <Typography variant="h6" fontWeight={700} color="#1a1a1a">
              강사 목록
            </Typography>
            <Typography variant="body2" color="#888" mt={0.25}>
              강사 소개 페이지에 노출되는 강사를 관리합니다.
            </Typography>
          </div>
          <Button
            variant="contained"
            color="pink"
            startIcon={<AddIcon />}
            onClick={() => window.open('/instructor/new')}
            sx={{ fontWeight: 700, borderRadius: '8px', px: 2.5, height: 40, whiteSpace: 'nowrap' }}
          >
            강사 추가
          </Button>
        </div>

        {usingMock && <div className="instructor-list__mock-banner">⚠ API 미연동 상태 — mock 데이터 표시 중</div>}

        {/* 검색 카드 */}
        <Paper elevation={0} className="instructor-list__search-card">
          <Input
            value={inputText}
            placeholder="이름 또는 전문분야로 검색"
            color="pink"
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onClickSearch()}
            disableUnderline
            className="instructor-list__search-input"
          />
          <Button
            variant="contained"
            color="pink"
            onClick={onClickSearch}
            endIcon={<SearchIcon fontSize="small" />}
            sx={{ borderRadius: '6px', height: 36, fontWeight: 600, fontSize: '0.8125rem' }}
          >
            검색
          </Button>
        </Paper>

        {/* 테이블 카드 */}
        <Paper elevation={0} className="instructor-list__table-card">
          <div className="instructor-list__table-top">
            <span>
              전체 <strong>{total}</strong>명
            </span>
          </div>

          <Box>
            <TableContainer>
              <Table stickyHeader>
                <EnhancedTableHead
                  headCells={HEAD_CELLS}
                  order={sortInfo.order}
                  orderBy={sortInfo.name}
                  onRequestSort={handleRequestSort}
                  rowCount={total}
                  numSelected={0}
                  color="pink"
                  disableSortHeaders={['tutorId', 'isVisible', 'actions']}
                />
                <TableBody>
                  {instructors.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#aaa', fontSize: '0.875rem' }}>
                        검색 결과가 없습니다.
                      </TableCell>
                    </TableRow>
                  ) : (
                    instructors.map((instructor) => (
                      <TableRow key={instructor.id} hover>
                        <TableCell align="center" sx={{ color: '#999', fontSize: '0.8125rem' }}>
                          {instructor.id}
                        </TableCell>
                        <TableCell align="left">
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar
                              src={instructor.profileImageUrl || undefined}
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: '0.8rem',
                                bgcolor: avatarColor(instructor.name),
                                '& img': { objectPosition: 'top' },
                              }}
                            >
                              {instructor.name?.[0]}
                            </Avatar>
                            <Typography fontSize="0.875rem" fontWeight={500}>
                              {instructor.name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell align="left">
                          <Typography fontSize="0.875rem" color="#555">
                            {instructor.specialty || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <div
                            className={`instructor-list__tutor-select${
                              !instructor.tutorId ? ' instructor-list__tutor-select--empty' : ''
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SelectSearch
                              options={tutorOptions}
                              value={instructor.tutorId ? String(instructor.tutorId) : ''}
                              onChange={(value) => onChangeTutor(instructor, value)}
                              placeholder="강사 검색"
                            />
                          </div>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                            <Switch
                              size="small"
                              color="pink"
                              checked={!!instructor.isVisible}
                              onChange={() => onToggleVisibility(instructor)}
                            />
                            <Chip
                              label={instructor.isVisible ? '노출' : '비노출'}
                              size="small"
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                bgcolor: instructor.isVisible ? '#edfbf4' : '#f5f5f5',
                                color: instructor.isVisible ? '#00875a' : '#999',
                                border: 'none',
                              }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <Tooltip title="수정">
                              <Button
                                size="small"
                                variant="outlined"
                                color="pink"
                                onClick={() => window.open(`/instructor/new/${instructor.id}`)}
                                sx={{ minWidth: 0, px: 1, py: 0.5, borderRadius: '6px' }}
                              >
                                <EditOutlinedIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                            <Tooltip title="삭제">
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => onClickDelete(instructor)}
                                sx={{ minWidth: 0, px: 1, py: 0.5, borderRadius: '6px' }}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <TablePagination
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50, 100]}
            showFirstButton
            showLastButton
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            labelRowsPerPage="페이지당 :"
            component="div"
          />
        </Paper>
      </div>
    </ThemeProvider>
  );
}

export default InstructorList;
